import type { TaskItem } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get } from "../api";
import { TaskDialog, TaskRows } from "../components/schedule";
import { Alert, Button, Empty, Loading, PageHeader, Panel, SelectField } from "../components/ui";
import { useUsers } from "../lookups";
import { useSession } from "../session";

/** Tasks: your own, or (with "manage everyone's tasks") anyone's. */
export function TasksPage() {
  const { can, me } = useSession();
  const qc = useQueryClient();
  const users = useUsers();
  const manageAll = can("tasks.manage_all");
  const [who, setWho] = useState("mine");
  const [status, setStatus] = useState("open");
  const [adding, setAdding] = useState(false);
  const qs = new URLSearchParams({ status, ...(who === "mine" ? {} : who === "all" ? { scope: "all" } : { assigneeId: who }) });
  const tasks = useQuery({ queryKey: ["tasks", "page", qs.toString()], queryFn: () => get<TaskItem[]>(`/api/tasks?${qs}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });

  return (
    <div>
      <PageHeader
        title="Tasks"
        description="High priority first, then by due date. Overdue ones are in red."
        action={
          <Button variant="primary" onClick={() => setAdding(true)}>
            + New task
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-3">
        {manageAll && (
          <SelectField label="Whose" value={who} onChange={(e) => setWho(e.target.value)} className="min-w-48">
            <option value="mine">Mine</option>
            <option value="all">Everyone's</option>
            {users.data
              ?.filter((u) => u.active && u.id !== me?.user.id)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </SelectField>
        )}
        <SelectField label="Show" value={status} onChange={(e) => setStatus(e.target.value)} className="min-w-40">
          <option value="open">Open</option>
          <option value="done">Completed</option>
          <option value="all">All</option>
        </SelectField>
      </div>
      {tasks.isLoading ? (
        <Loading />
      ) : tasks.error ? (
        <Alert>{errorMessage(tasks.error)}</Alert>
      ) : !tasks.data?.length ? (
        <Panel>
          <Empty>{status === "open" ? "Nothing to do. Nice." : "No tasks."}</Empty>
        </Panel>
      ) : (
        <Panel>
          <TaskRows tasks={tasks.data} showAssignee={who !== "mine"} onChanged={refresh} />
        </Panel>
      )}
      {adding && <TaskDialog onClose={() => setAdding(false)} onSaved={refresh} />}
    </div>
  );
}
